import {
    DEFAULT_LOCALE,
    LEGACY_LOCALE_PREFIXES,
    LOCALE_CONFIG,
    LOCALES,
    type EnterHomeLocale,
} from '#i18n/locale-config';

export { DEFAULT_LOCALE, LOCALES, LOCALE_CONFIG, LEGACY_LOCALE_PREFIXES, isSupportedLocale } from '#i18n/locale-config';
export type { EnterHomeLocale, HomeLocaleSnapshot } from '#i18n/locale-config';

const DEFAULT_BASE_URL = 'https://enter.converge.ai';
const ENTER_MARKETPLACE_PATH = '/enter/marketplace';

export interface EnterMarketplacePathResult {
    pathname: string;
    prefixed: boolean;
}

export interface BuildLocalizedPathOptions {
    /** Keep the Marketplace URL in the locale-first `/enter` mount. */
    productPrefixed?: boolean;
}

function getLocalePrefixes(locale: EnterHomeLocale): string[] {
    const activePrefix = LOCALE_CONFIG[locale].pathPrefix;
    const legacyPrefix = LEGACY_LOCALE_PREFIXES[locale];

    return [activePrefix, legacyPrefix].filter((prefix): prefix is string => Boolean(prefix));
}

export function normalizeBaseUrl(value?: string): string {
    return (value || DEFAULT_BASE_URL).replace(/\/$/, '');
}

export function stripLocalePrefix(pathname: string): string {
    const normalizedPathname = pathname.startsWith('/') ? pathname : `/${pathname}`;
    const match = LOCALES.flatMap((locale) => getLocalePrefixes(locale).map((prefix) => ({ locale, prefix }))).find(
        ({ prefix }) => normalizedPathname === prefix || normalizedPathname.startsWith(`${prefix}/`),
    );

    if (!match) {
        return normalizedPathname || '/';
    }

    const strippedPath = normalizedPathname.slice(match.prefix.length);
    return strippedPath || '/';
}

function isMarketplacePath(pathname: string): boolean {
    return pathname === '/marketplace' || pathname.startsWith('/marketplace/');
}

/** Remove the locale-first `/enter` mount from a Marketplace pathname. */
export function stripEnterMarketplacePrefix(pathname: string): EnterMarketplacePathResult {
    const normalizedPathname = pathname.startsWith('/') ? pathname : `/${pathname}`;

    if (
        normalizedPathname === ENTER_MARKETPLACE_PATH ||
        normalizedPathname.startsWith(`${ENTER_MARKETPLACE_PATH}/`)
    ) {
        return {
            pathname: normalizedPathname.slice('/enter'.length) || '/',
            prefixed: true,
        };
    }

    for (const locale of LOCALES) {
        for (const localePrefix of getLocalePrefixes(locale)) {
            const prefixedPath = `${localePrefix}${ENTER_MARKETPLACE_PATH}`;
            if (
                normalizedPathname === prefixedPath ||
                normalizedPathname.startsWith(`${prefixedPath}/`)
            ) {
                return {
                    pathname: `${localePrefix}${normalizedPathname.slice(prefixedPath.length - '/marketplace'.length)}`,
                    prefixed: true,
                };
            }
        }
    }

    return { pathname: normalizedPathname, prefixed: false };
}

export function buildLocalizedPath(
    locale: EnterHomeLocale,
    pathname: string,
    options: BuildLocalizedPathOptions = {}
): string {
    const productPath = stripEnterMarketplacePrefix(pathname);
    const normalizedPathname = stripLocalePrefix(productPath.pathname);
    const productPrefixed = options.productPrefixed ?? productPath.prefixed;

    const localizedPath = locale === DEFAULT_LOCALE
        ? normalizedPathname
        : `${LOCALE_CONFIG[locale].pathPrefix}${normalizedPathname === '/' ? '' : normalizedPathname}`;

    if (productPrefixed && isMarketplacePath(normalizedPathname)) {
        const localePrefix = locale === DEFAULT_LOCALE ? '' : LOCALE_CONFIG[locale].pathPrefix;
        return `${localePrefix}/enter${normalizedPathname}`;
    }

    return localizedPath || '/';
}

export function buildLocalizedUrl(
    locale: EnterHomeLocale,
    pathname: string,
    baseUrl?: string,
    options?: BuildLocalizedPathOptions
): string {
    return `${normalizeBaseUrl(baseUrl)}${buildLocalizedPath(locale, pathname, options)}`;
}

export function normalizeLegacyLocalePath(pathname: string): string | null {
    const normalizedPathname = pathname.startsWith('/') ? pathname : `/${pathname}`;

    for (const locale of LOCALES) {
        const legacyPrefix = LEGACY_LOCALE_PREFIXES[locale];
        const currentPrefix = LOCALE_CONFIG[locale].pathPrefix;

        if (!legacyPrefix || legacyPrefix === currentPrefix) {
            continue;
        }

        if (normalizedPathname === legacyPrefix) {
            return currentPrefix || '/';
        }

        if (normalizedPathname.startsWith(`${legacyPrefix}/`)) {
            return `${currentPrefix}${normalizedPathname.slice(legacyPrefix.length)}`;
        }
    }

    return null;
}

/** 只保留最高权重的语言，避免 best-fit 改选用户的次要语言。 */
export function getTopAcceptLanguage(header: string | null): string | null {
    let bestLanguage: string | null = null;
    let bestQuality = 0;
    for (const entry of header?.split(',') ?? []) {
        const match = entry
            .trim()
            .match(/^([a-z]{1,8}(?:-[a-z0-9]{1,8})*|\*)(?:\s*;\s*q\s*=\s*(0(?:\.\d{0,3})?|1(?:\.0{0,3})?))?$/i);
        if (!match) continue;
        const quality = match[2] === undefined ? 1 : Number(match[2]);
        if (quality > bestQuality) {
            bestQuality = quality;
            bestLanguage = match[1];
        }
    }
    return bestLanguage === '*' ? null : bestLanguage;
}
